import { useState, useMemo } from "react";
import { FaSearch, FaFileArchive, FaUserFriends } from "react-icons/fa";
import CartaPreview from "../components/reportes/CartaPreview";
import Modal from "../components/Modal";

/* COMPONENTES */
const FiltroCartas = ({
  fechaInicio,
  fechaFin,
  setFechaInicio,
  setFechaFin,
  onBuscar,
  onGenerar,
  generando,
}) => {
  const inputClass =
    "border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400";

  return (
    <div className="flex flex-wrap gap-3 items-end">
      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Fecha inicio</label>
        <input
          type="date"
          value={fechaInicio}
          onChange={(e) => setFechaInicio(e.target.value)}
          className={inputClass}
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-700 mb-1">Fecha fin</label>
        <input
          type="date"
          value={fechaFin}
          onChange={(e) => setFechaFin(e.target.value)}
          className={inputClass}
        />
      </div>

      <button
        onClick={onBuscar}
        className="flex items-center gap-2 bg-white border border-slate-200 text-slate-600 text-sm font-medium px-4 py-2 rounded-lg hover:bg-slate-50 transition"
      >
        <FaSearch className="text-xs" /> Buscar
      </button>

      <button
        onClick={onGenerar}
        disabled={generando}
        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
      >
        <FaFileArchive className="text-xs" /> {generando ? "Generando..." : "Generar Zip de Cartas"}
      </button>
    </div>
  );
};

const ListaMiembrosCartas = ({ miembros, seleccionado, onSelect }) => {
  const [busqueda, setBusqueda] = useState("");

  const miembrosFiltrados = useMemo(() => {
    const termino = busqueda.trim().toLowerCase();
    if (!termino) return miembros;
    return miembros.filter((m) => m.nombre?.toLowerCase().includes(termino));
  }, [miembros, busqueda]);

  return (
    <div>
      <div className="relative mb-3">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs" />
        <input
          type="text"
          placeholder="Buscar persona..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
        />
      </div>

      <div className="text-xs font-medium text-slate-500 mb-2">
        {miembrosFiltrados.length} persona{miembrosFiltrados.length === 1 ? "" : "s"} con contribución
      </div>

      <div className="space-y-1 max-h-[520px] overflow-y-auto">
        {miembrosFiltrados.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">Sin resultados</p>
        ) : (
          miembrosFiltrados.map((m) => (
            <div
              key={m.id_miembro}
              onClick={() => onSelect(m)}
              className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg cursor-pointer transition ${
                seleccionado?.id_miembro === m.id_miembro
                  ? "bg-indigo-50 text-indigo-700"
                  : "hover:bg-slate-50 text-slate-700"
              }`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className="w-7 h-7 rounded-md bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                  <FaUserFriends className="text-xs" />
                </span>
                <span className="truncate text-sm font-medium">{m.nombre}</span>
              </span>
              <span className="text-sm font-semibold shrink-0">${Number(m.total || 0).toFixed(2)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

/* ================== PAGE ================== */

const CartasAgradecimiento = () => {

  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");

  const [miembros, setMiembros] = useState([]);
  const [preview, setPreview] = useState(null);

  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ mostrar: false, mensaje: "", tipo: "success" });

  const [generando, setGenerando] = useState(false);

  const mostrarToast = (mensaje, tipo = "success") => {
    setToast({ mostrar: true, mensaje, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: "", tipo: "success" }), 3000);
  };

  const obtenerPreview = async () => {
    if (!fechaInicio || !fechaFin) {
      mostrarToast("Selecciona un rango de fechas", "error");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("/api/cartas/preview", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin
        })
      });

      if (!res.ok) throw new Error("Error al obtener datos");

      const data = await res.json();

      setMiembros(data || []);
      setPreview(data?.[0] || null);

    } catch (err) {
      console.error(err);
      mostrarToast("Error al obtener datos", "error");
    } finally {
      setLoading(false);
    }
  };

  const generarCartas = async () => {
    if (!fechaInicio || !fechaFin) {
      mostrarToast("Selecciona un rango de fechas", "error");
      return;
    }

    try {
      setGenerando(true);

      const res = await fetch("/api/cartas/generar", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fecha_inicio: fechaInicio,
          fecha_fin: fechaFin
        })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        mostrarToast(err.error || "Error al generar las cartas", "error");
        return;
      }

      const blob = await res.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `cartas_${fechaInicio}_${fechaFin}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error(err);
      mostrarToast("Error al generar las cartas", "error");
    } finally {
      setGenerando(false);
    }
  };

  return (
    <div className="p-6 bg-slate-50">

      {/* SPINNER OVERLAY */}
      <Modal open={generando} onClose={() => {}}>
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <div className="text-slate-700 text-sm font-medium">
              Generando cartas...
            </div>
          </div>
      </Modal>

      {/* HEADER */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Cartas de Agradecimiento</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Genera cartas de agradecimiento por contribuciones dentro de un rango de fechas.
        </p>
      </div>

      {toast.mostrar && (
        <div className={`fixed top-5 right-5 z-[70] px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.tipo === "success" ? "bg-emerald-600" : "bg-rose-600"}`}>
          {toast.mensaje}
        </div>
      )}

      {/* FILTRO */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <FiltroCartas
          fechaInicio={fechaInicio}
          fechaFin={fechaFin}
          setFechaInicio={setFechaInicio}
          setFechaFin={setFechaFin}
          onBuscar={obtenerPreview}
          onGenerar={generarCartas}
          generando={generando}
        />
      </div>

      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 py-16 text-center text-sm text-slate-400">
          Cargando...
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

          <div className="lg:col-span-4 bg-white rounded-xl shadow-sm border border-slate-200 p-4 h-fit">
            <ListaMiembrosCartas
              miembros={miembros}
              seleccionado={preview}
              onSelect={setPreview}
            />
          </div>

          <div className="lg:col-span-8 flex justify-center">
            {preview ? (
              <CartaPreview
                data={preview}
                fechaInicio={fechaInicio}
                fechaFin={fechaFin}
              />
            ) : (
              <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 py-20 text-center text-sm text-slate-400">
                Selecciona un miembro para ver la carta
              </div>
            )}
          </div>

        </div>
      )}

    </div>
  );
};

export default CartasAgradecimiento;
